import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const BibliaAsignaciones = ({ userRole }) => {
  const API = `${window.location.origin}/api`;
  const [assignments, setAssignments] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [assignToAll, setAssignToAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    book_id: 'juan',
    chapter_start: 1,
    chapter_end: null,
    questions: [''],
    due_date: '',
    student_ids: []
  });

  // Completion states
  const [answers, setAnswers] = useState([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadAssignments();
    if (userRole === 'admin' || userRole === 'pastor' || userRole === 'profesor') {
      loadAllStudents();
    }
  }, [userRole]);

  const loadAllStudents = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const usersData = response.data.users || response.data || [];
      // Filtrar solo estudiantes ACTIVOS
      const students = usersData.filter(u => 
        ['estudiante', 'pastor', 'graduado'].includes(u.rol) && u.activo !== false
      );
      setAllStudents(students);
    } catch (error) {
      console.error('Error loading students:', error);
      setAllStudents([]);
    }
  };

  const toggleStudent = (studentId) => {
    setFormData(prev => {
      const ids = prev.student_ids || [];
      if (ids.includes(studentId)) {
        return { ...prev, student_ids: ids.filter(id => id !== studentId) };
      } else {
        return { ...prev, student_ids: [...ids, studentId] };
      }
    });
  };

  const selectAllStudents = () => {
    setFormData(prev => ({
      ...prev,
      student_ids: allStudents.map(s => s.id || s.user_id)
    }));
  };

  const deselectAllStudents = () => {
    setFormData(prev => ({ ...prev, student_ids: [] }));
  };

  const filteredStudents = allStudents.filter(s => {
    const nombre = `${s.nombre || ''} ${s.apellido || ''}`.toLowerCase();
    return nombre.includes(searchTerm.toLowerCase());
  });

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data.assignments);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast.error('Error al cargar asignaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      
      // Filtrar preguntas vacías
      const filteredQuestions = formData.questions.filter(q => q.trim() !== '');
      
      // Si "Asignar a todos" está activado, obtener todos los IDs de estudiantes activos
      let studentIds = formData.student_ids;
      if (assignToAll) {
        studentIds = allStudents.map(s => s.id || s.user_id);
      }
      
      if (editingAssignment) {
        // Actualizar asignación existente
        await axios.put(
          `${API}/bible/assignments/${editingAssignment.assignment_id}`,
          { ...formData, questions: filteredQuestions, student_ids: studentIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Asignación actualizada exitosamente');
      } else {
        // Crear nueva asignación
        await axios.post(
          `${API}/bible/assignments`,
          { ...formData, questions: filteredQuestions, student_ids: studentIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Asignación creada exitosamente');
      }
      
      setShowCreateForm(false);
      setEditingAssignment(null);
      setAssignToAll(false);
      setSearchTerm('');
      setFormData({
        title: '',
        description: '',
        book_id: 'juan',
        chapter_start: 1,
        chapter_end: null,
        questions: [''],
        due_date: '',
        student_ids: []
      });
      loadAssignments();
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error('Error al guardar asignación');
    }
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      title: assignment.title,
      description: assignment.description,
      book_id: assignment.book_id,
      chapter_start: assignment.chapter_start,
      chapter_end: assignment.chapter_end,
      verse_start: assignment.verse_start,
      verse_end: assignment.verse_end,
      questions: assignment.questions.length > 0 ? assignment.questions : [''],
      due_date: assignment.due_date,
      student_ids: assignment.student_ids || []
    });
    setShowCreateForm(true);
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta asignación? Se eliminarán también todas las respuestas de estudiantes.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/bible/assignments/${assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asignación eliminada exitosamente');
      loadAssignments();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Error al eliminar asignación');
    }
  };

  const handleCompleteAssignment = async (assignmentId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/bible/assignments/complete`,
        {
          assignment_id: assignmentId,
          answers: answers,
          notes: notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Asignación completada exitosamente');
      setSelectedAssignment(null);
      setAnswers([]);
      setNotes('');
      loadAssignments();
    } catch (error) {
      console.error('Error completing assignment:', error);
      toast.error('Error al completar asignación');
    }
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, '']
    });
  };

  const updateQuestion = (index, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = value;
    setFormData({ ...formData, questions: newQuestions });
  };

  const removeQuestion = (index) => {
    const newQuestions = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: newQuestions });
  };

  const isProfessor = userRole === 'profesor' || userRole === 'admin' || userRole === 'pastor';

  // Vista de detalles de asignación para estudiantes
  if (selectedAssignment && !isProfessor) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <button
          onClick={() => {
            setSelectedAssignment(null);
            setAnswers([]);
            setNotes('');
          }}
          className="mb-4 text-indigo-600 hover:text-indigo-800 font-semibold"
        >
          ← Volver a asignaciones
        </button>

        <h2 className="text-3xl font-bold text-gray-800 mb-2">{selectedAssignment.title}</h2>
        <p className="text-gray-600 mb-4">{selectedAssignment.description}</p>

        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
          <h3 className="font-bold text-indigo-800 mb-2">📖 Lectura Asignada</h3>
          <p className="text-indigo-700">
            {selectedAssignment.book_id.toUpperCase()} Capítulo {selectedAssignment.chapter_start}
            {selectedAssignment.chapter_end && ` - ${selectedAssignment.chapter_end}`}
          </p>
          <p className="text-sm text-indigo-600 mt-2">
            📅 Fecha límite: {new Date(selectedAssignment.due_date).toLocaleDateString('es-ES')}
          </p>
        </div>

        {selectedAssignment.questions && selectedAssignment.questions.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Preguntas de Reflexión</h3>
            {selectedAssignment.questions.map((question, index) => (
              <div key={index} className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  {index + 1}. {question}
                </label>
                <textarea
                  value={answers[index] || ''}
                  onChange={(e) => {
                    const newAnswers = [...answers];
                    newAnswers[index] = e.target.value;
                    setAnswers(newAnswers);
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
                  rows="3"
                  placeholder="Escribe tu respuesta aquí..."
                />
              </div>
            ))}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-gray-700 font-semibold mb-2">Notas adicionales (opcional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
            rows="3"
            placeholder="Agrega tus reflexiones o notas personales..."
          />
        </div>

        <button
          onClick={() => handleCompleteAssignment(selectedAssignment.assignment_id)}
          className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
        >
          Marcar como Completada
        </button>
      </div>
    );
  }

  // Formulario de creación para profesores
  if (showCreateForm && isProfessor) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <button
          onClick={() => {
            setShowCreateForm(false);
            setEditingAssignment(null);
            setFormData({
              title: '',
              description: '',
              book_id: 'juan',
              chapter_start: 1,
              chapter_end: null,
              questions: [''],
              due_date: '',
              student_ids: []
            });
          }}
          className="mb-4 text-indigo-600 hover:text-indigo-800 font-semibold"
        >
          ← Cancelar
        </button>

        <h2 className="text-3xl font-bold text-gray-800 mb-6">
          {editingAssignment ? 'Editar Asignación Bíblica' : 'Nueva Asignación Bíblica'}
        </h2>

        <form onSubmit={handleCreateAssignment} className="space-y-6">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Título de la Asignación</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
              placeholder="Ej: Estudio de Juan 3"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Descripción</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
              rows="3"
              placeholder="Describe la asignación..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Libro (ID)</label>
              <input
                type="text"
                required
                value={formData.book_id}
                onChange={(e) => setFormData({ ...formData, book_id: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
                placeholder="juan, mat, sal..."
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Capítulo Inicial</label>
              <input
                type="number"
                required
                min="1"
                value={formData.chapter_start}
                onChange={(e) => setFormData({ ...formData, chapter_start: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Capítulo Final (opcional)</label>
              <input
                type="number"
                min="1"
                value={formData.chapter_end || ''}
                onChange={(e) => setFormData({ ...formData, chapter_end: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Fecha Límite</label>
            <input
              type="date"
              required
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Preguntas de Reflexión</label>
            {formData.questions.map((question, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={question}
                  onChange={(e) => updateQuestion(index, e.target.value)}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none"
                  placeholder={`Pregunta ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeQuestion(index)}
                  className="px-4 py-3 bg-red-100 text-red-600 rounded-xl hover:bg-red-200"
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addQuestion}
              className="mt-2 px-4 py-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200"
            >
              + Agregar Pregunta
            </button>
          </div>

          {(userRole === 'admin' || userRole === 'pastor' || userRole === 'profesor') && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-xl p-4">
              <label className="block text-gray-800 font-semibold mb-3">
                Asignar a Estudiantes ({allStudents.length} activos)
              </label>
              
              {/* Botones rapidos */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => { setAssignToAll(true); selectAllStudents(); }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    assignToAll ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-50'
                  }`}
                  data-testid="assign-all-btn"
                >
                  Todos ({allStudents.length})
                </button>
                <button
                  type="button"
                  onClick={() => { setAssignToAll(false); deselectAllStudents(); }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 transition-all"
                  data-testid="deselect-all-btn"
                >
                  Ninguno
                </button>
                {!assignToAll && formData.student_ids.length > 0 && (
                  <span className="flex items-center text-sm text-indigo-700 font-bold">
                    {formData.student_ids.length} seleccionado(s)
                  </span>
                )}
              </div>

              {/* Buscador */}
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar estudiante por nombre..."
                className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-lg text-sm focus:border-indigo-500 focus:outline-none"
                data-testid="student-search-input"
              />

              {/* Lista de estudiantes */}
              <div className="max-h-48 overflow-y-auto bg-white rounded-lg border border-gray-200">
                {filteredStudents.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-4">No se encontraron estudiantes</p>
                ) : (
                  filteredStudents.map(student => {
                    const studentId = student.id || student.user_id;
                    const isSelected = assignToAll || (formData.student_ids || []).includes(studentId);
                    return (
                      <label
                        key={studentId}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                          isSelected ? 'bg-indigo-50' : ''
                        }`}
                        data-testid={`student-option-${studentId}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            if (assignToAll) {
                              setAssignToAll(false);
                              const allIds = allStudents.map(s => s.id || s.user_id).filter(id => id !== studentId);
                              setFormData(prev => ({ ...prev, student_ids: allIds }));
                            } else {
                              toggleStudent(studentId);
                            }
                          }}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-800 block truncate">
                            {student.nombre} {student.apellido}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 capitalize">{student.rol}</span>
                      </label>
                    );
                  })
                )}
              </div>
              
              {!assignToAll && formData.student_ids.length === 0 && (
                <p className="text-xs text-orange-600 mt-2 font-semibold">
                  Selecciona al menos un estudiante o usa "Todos"
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            {editingAssignment ? 'Actualizar Asignación' : 'Crear Asignación'}
          </button>
        </form>
      </div>
    );
  }

  // Lista de asignaciones
  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center">
          <span className="text-4xl mr-3">📝</span>
          Asignaciones Bíblicas
        </h2>
        
        {isProfessor && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            + Nueva Asignación
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-600">Cargando asignaciones...</div>
      ) : assignments.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📚</div>
          <p className="text-gray-600">
            {isProfessor ? 'No has creado asignaciones aún' : 'No tienes asignaciones pendientes'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {assignments.map(assignment => (
            <div
              key={assignment.assignment_id}
              className="border-2 border-gray-200 rounded-xl p-6 hover:border-indigo-500 hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-xl font-bold text-gray-800">{assignment.title}</h3>
                {!isProfessor && assignment.completed && (
                  <span className="text-2xl">✅</span>
                )}
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{assignment.description}</p>

              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                <p className="text-gray-700">
                  📖 {assignment.book_id.toUpperCase()} {assignment.chapter_start}
                  {assignment.chapter_end && ` - ${assignment.chapter_end}`}
                </p>
                <p className="text-gray-600 mt-1">
                  📅 {new Date(assignment.due_date).toLocaleDateString('es-ES')}
                </p>
              </div>

              {isProfessor ? (
                <div>
                  <div className="text-sm text-gray-600 mb-3">
                    <p>👥 {assignment.completions_count || 0} / {assignment.total_students || 0} completadas</p>
                    <p className="text-xs text-gray-500 mt-1">Profesor: {assignment.professor_name}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditAssignment(assignment)}
                      className="flex-1 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-all"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => handleDeleteAssignment(assignment.assignment_id)}
                      className="flex-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-all"
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setSelectedAssignment(assignment)}
                  className={`w-full px-4 py-2 rounded-lg font-semibold transition-all ${
                    assignment.completed
                      ? 'bg-green-100 text-green-700'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                  disabled={assignment.completed}
                >
                  {assignment.completed ? 'Completada ✓' : 'Ver Asignación'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BibliaAsignaciones;
